/**
 * Custom hooks for the church management app
 */
import { useState, useEffect, useRef, useCallback } from 'react';

// Cache configuration
export const CACHE_VERSION = "v2.1.0";
export const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * useLocalState - Persists state to localStorage with cache TTL
 * @param {string} key - Storage key
 * @param {any} initial - Initial value
 * @param {object} options - { ttl: number, skipCache: boolean }
 */
export const useLocalState = (key, initial, options = {}) => {
  const { ttl = CACHE_TTL, skipCache = false } = options;
  const versionedKey = `${CACHE_VERSION}.${key}`;
  const isFirstRender = useRef(true);
  
  const [val, setVal] = useState(() => {
    if (skipCache) return initial;
    try {
      const cached = localStorage.getItem(versionedKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Check if cache is still valid
        if (timestamp && Date.now() - timestamp < ttl) {
          return data;
        }
        // Cache expired, clear it
        localStorage.removeItem(versionedKey);
      }
      return initial;
    } catch {
      return initial;
    }
  });
  
  useEffect(() => {
    if (skipCache) return;
    // Skip writing on first render to avoid overwriting fresh login data
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try { 
      localStorage.setItem(versionedKey, JSON.stringify({
        data: val,
        timestamp: Date.now()
      })); 
    } catch {}
  }, [versionedKey, val, skipCache]);
  
  return [val, setVal];
};

/**
 * Clear old cache versions on load
 */
export const clearOldCache = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      // Remove old unversioned cache keys
      if (key.startsWith('church.') || key.startsWith('choir.') || key.startsWith('attendance.')) {
        if (!key.startsWith(CACHE_VERSION)) {
          localStorage.removeItem(key);
        }
      }
    });
  } catch {}
};

/**
 * useDebounce - Debounce a value
 */
export const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

/**
 * useToggle - Simple toggle hook
 */
export const useToggle = (initialValue = false) => {
  const [value, setValue] = useState(initialValue);
  const toggle = useCallback(() => setValue(v => !v), []);
  return [value, toggle, setValue];
};

/**
 * useFetch - Simple data fetching hook
 */
export const useFetch = (url, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) return;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(url, options);
        if (!response.ok) throw new Error('Network response was not ok');
        const result = await response.json();
        setData(result);
        setError(null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [url]);

  return { data, loading, error, refetch: () => {} };
};

export default {
  useLocalState,
  useDebounce,
  useToggle,
  useFetch,
  clearOldCache,
  CACHE_VERSION,
  CACHE_TTL
};
